// Client-side model descriptor. Shape mirrors gateway ClientSafeModel (types.ts)
// exactly — supportsEffort is REQUIRED because the gateway always sends it.
// The pre-connect seed (AVAILABLE_MODELS below) must also populate it.
export interface ModelEntry {
  id: string;
  label: string;
  /** Which subprocess adapter handles this model — the C4 group heading
   *  (CLAUDE CODE / CODEX CLI / HERMES) in every model-selection surface. */
  substrate: "claude-code" | "codex" | "openrouter" | "hermes";
  provider: "anthropic" | "openai" | "google" | "moonshot" | "deepseek" | "x-ai" | "qwen" | "openrouter" | "minimax";
  /** How a self-hoster authenticates to run this row — 'subscription' /
   *  'API key' / 'login' fact in the C4 subline. */
  authKind: "subscription" | "api-key" | "login";
  /** True when running this row costs nothing beyond what's already paid
   *  for — shown in place of the auth-kind fact when true. */
  free: boolean;
  /** The exact string the underlying provider actually runs — the "exact
   *  model id" fact in the C4 subline. */
  resolvedModel: string;
  supportsReasoning: boolean;
  nativeReasoning?: boolean;
  /** Whether the effort pill is shown for this model. Always set — sent from
   *  the gateway registry in the init frame; explicitly provided in the seed. */
  supportsEffort: boolean;
  minTier: "creator" | "vibecoder";
  /** True for a Claude Code rolling-alias row ("Opus · latest" etc). Sent by
   *  the gateway so an existing session/sticky pick on an alias still
   *  resolves via useChatConfig's unfiltered baseModels; the two picker
   *  render sites (InputControls.tsx, ModelsPanel.tsx) filter these out of
   *  what's actually offered. */
  rollingAlias?: boolean;
}

/**
 * Resolve the label shown by every model surface.
 *
 * `serverModel` is the last model acknowledged by init/reconnect/ready. Local
 * config may already contain a requested model while its confirmation dialog or
 * provider swap is still in flight, so it is only the cold-render fallback.
 */
export function resolveDisplayedModel(
  serverModel: string | null,
  availableModels: ModelEntry[],
  localFallback: ModelEntry,
): ModelEntry {
  if (!serverModel) return localFallback;
  return availableModels.find((model) => model.id === serverModel) ?? localFallback;
}

// Pre-connect fallback seed — used by the picker BEFORE the gateway's init
// frame arrives. After init, useChatConfig replaces this with the live
// registry from the server (msg.models). The authoritative list lives in
// src/gateway/model-registry.ts; this is only a best-effort approximation.
//
// supportsReasoning = true  → UI thinking toggle is shown.
// nativeReasoning = true    → thinking is auto-enabled for always-on reasoning models.
// supportsEffort = true     → effort pill is shown (required field — must be set).
//
// Only list models the gateway currently serves. OpenAI/Codex rows return when
// Phase 3 ships the Codex adapter and the gateway advertises them via init.
//
// SYNC: keep the top entry aligned with gateway/model-registry.ts DEFAULT_MODEL_ID.
// The seed is a best-effort hint; the live registry (init frame) is authoritative.
// authKind: "login" and free: false on every seed row below — all pinned
// claude-code rows authenticate via the Claude Code CLI's own login on the
// box, never a per-model API key. resolvedModel === id for every pinned
// (non-rolling-alias) claude-code row in gateway/model-registry.ts — kept
// that way here deliberately (SYNC comment above).
export const AVAILABLE_MODELS: ModelEntry[] = [
  // ─── Rook — Claude Max subscription ──────────────────────────────────
  // supportsReasoning: false on every 5.x row (Opus 5/5.5, Fable 5/5.1) — all
  // current 5.x models AUP-flag the sentinel reasoning instruction (verified
  // 2026-10-03, gateway/model-registry.ts sentinelReasoningUnsafe doc). The
  // gateway registry gate is the actual protection; this only hides the toggle.
  { id: "claude-opus-5-5", label: "Opus 5.5 · 1M", substrate: "claude-code", provider: "anthropic", authKind: "login", free: false, resolvedModel: "claude-opus-5-5", supportsReasoning: false, supportsEffort: true, minTier: "creator" },
  { id: "claude-opus-5", label: "Opus 5 · 1M", substrate: "claude-code", provider: "anthropic", authKind: "login", free: false, resolvedModel: "claude-opus-5", supportsReasoning: false, supportsEffort: true, minTier: "creator" },
  { id: "claude-fable-5-1", label: "Fable 5.1", substrate: "claude-code", provider: "anthropic", authKind: "login", free: false, resolvedModel: "claude-fable-5-1", supportsReasoning: false, supportsEffort: true, minTier: "creator" },
  { id: "claude-fable-5", label: "Fable 5", substrate: "claude-code", provider: "anthropic", authKind: "login", free: false, resolvedModel: "claude-fable-5", supportsReasoning: false, supportsEffort: true, minTier: "creator" },
  { id: "claude-opus-4-8", label: "Opus 4.8 · 1M", substrate: "claude-code", provider: "anthropic", authKind: "login", free: false, resolvedModel: "claude-opus-4-8", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "claude-opus-4-7", label: "Opus 4.7 · 1M", substrate: "claude-code", provider: "anthropic", authKind: "login", free: false, resolvedModel: "claude-opus-4-7", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "claude-opus-4-6", label: "Opus 4.6", substrate: "claude-code", provider: "anthropic", authKind: "login", free: false, resolvedModel: "claude-opus-4-6", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "claude-sonnet-4-6", label: "Sonnet 4.6", substrate: "claude-code", provider: "anthropic", authKind: "login", free: false, resolvedModel: "claude-sonnet-4-6", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "claude-haiku-4-5", label: "Haiku 4.5", substrate: "claude-code", provider: "anthropic", authKind: "login", free: false, resolvedModel: "claude-haiku-4-5", supportsReasoning: false, supportsEffort: true, minTier: "creator" },
  // ─── OpenAI — Codex CLI (ChatGPT subscription, gated behind CODEX_ENABLED=1) ───
  // First codex row ever added to this fallback seed — the "Only list models
  // the gateway currently serves" comment above predates the CODEX_ENABLED gate
  // going live in gateway/model-registry.ts and is stale on that point; this row
  // is a best-effort pre-connect hint only and is replaced by the live init-frame
  // registry the moment the gateway connects (a no-op when CODEX_ENABLED is unset).
  // See gateway/model-registry.ts CODEX_GPT_6_ASTRA (added 2026-09-06) for the
  // verified wire name, rejected variants, and identity-lock caveat, and
  // CODEX_GPT_6_SOL for the codex-cli >= 0.160.0 prerequisite.
  { id: "gpt-6.1-sol", label: "GPT-6.1 Sol · Codex", substrate: "codex", provider: "openai", authKind: "subscription", free: false, resolvedModel: "gpt-6.1-sol", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "gpt-6-sol", label: "GPT-6 Sol · Codex", substrate: "codex", provider: "openai", authKind: "subscription", free: false, resolvedModel: "gpt-6-sol", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
  { id: "gpt-6-astra", label: "GPT-6 Astra · Codex", substrate: "codex", provider: "openai", authKind: "subscription", free: false, resolvedModel: "gpt-6-astra", supportsReasoning: true, supportsEffort: true, minTier: "creator" },
];

export const MODEL_IDS = new Set(AVAILABLE_MODELS.map((m) => m.id));

// SYNC: EffortLevel must match in gateway/types.ts and client/shared/chat-config.ts
export type EffortLevel = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

// Pre-connect seed model. Must match a model the gateway currently serves.
// After the init frame arrives, useChatConfig uses the live registry exclusively
// and never reads this constant as a reset target (see useChatConfig.ts).
// SYNC: must match gateway/model-registry.ts DEFAULT_MODEL_ID.
export const DEFAULT_MODEL_ID = "claude-opus-4-8";

export interface ChatConfig {
  model: string;
  effort: EffortLevel;
  thinking: boolean;
  autoSummarize: boolean;
}

export const DEFAULT_CHAT_CONFIG: ChatConfig = {
  model: DEFAULT_MODEL_ID,
  effort: 'medium',
  thinking: false,
  autoSummarize: false,
};
